import { api, ApiError } from "./apiClient";

export interface DevRolePreset {
  id: string;
  label: string;
  groups: string[];
}

export interface DevRoleSwitchState {
  enabled: boolean;
  active: boolean;
  groups: string[];
  groups_real: string[];
  can_manage: boolean;
  presets: DevRolePreset[];
  available_groups: string[];
  dev_preserve_groups?: string[];
  preset: string | null;
  impersonate_login?: string | null;
  simulate_birthday?: boolean;
  simulate_christmas?: boolean;
  simulate_halloween?: boolean;
  simulate_vacation?: boolean;
}

export interface DevRoleEmployee {
  login: string;
  displayName: string;
  groups: string[];
}

export interface DevRoleCatalog {
  employees: DevRoleEmployee[];
  groups: string[];
  employee_count: number;
  dev_preserve_groups: string[];
}

export type DevRoleSwitchFetchResult =
  | { ok: true; state: DevRoleSwitchState }
  | { ok: false; status?: number; message: string };

export async function fetchDevRoleSwitchState(): Promise<DevRoleSwitchFetchResult> {
  try {
    const data = await api.get<DevRoleSwitchState>("/dev/role-switch");
    if (!data?.enabled) {
      return {
        ok: false,
        message:
          "Rollenwechsel auf dieser API-Instanz deaktiviert (DEV_ROLE_SWITCH_ENABLED prüfen).",
      };
    }
    if (!data.can_manage) {
      return { ok: false, message: "Nur App-Admins dürfen Rollen simulieren." };
    }
    return { ok: true, state: data };
  } catch (err) {
    const status = err instanceof ApiError ? err.status : undefined;
    if (status === 404) {
      return {
        ok: false,
        status: 404,
        message:
          "Rollenwechsel hier deaktiviert oder Endpunkt fehlt (DEV_ROLE_SWITCH_ENABLED / Deploy prüfen).",
      };
    }
    if (status === 401) {
      return { ok: false, status: 401, message: "Nicht angemeldet (Session-Cookie fehlt)." };
    }
    return {
      ok: false,
      status,
      message:
        status != null
          ? `API antwortet mit HTTP ${status}.`
          : "API nicht erreichbar (Netzwerk/CORS).",
    };
  }
}

export async function applyDevRolePreset(presetId: string): Promise<void> {
  await api.put("/dev/role-switch", { preset: presetId });
}

export async function fetchDevRoleCatalog(q = ""): Promise<DevRoleCatalog> {
  const query = q.trim() ? `?q=${encodeURIComponent(q.trim())}` : "";
  return api.get<DevRoleCatalog>(`/dev/role-switch/catalog${query}`);
}

export interface DevRoleEmployeeSearchHit {
  id: string;
  login: string;
  label: string;
  email?: string | null;
  subtitle?: string | null;
  groups: string[];
}

export async function searchDevRoleEmployees(
  q: string,
  limit = 50,
): Promise<DevRoleEmployeeSearchHit[]> {
  const params = new URLSearchParams();
  if (q.trim()) params.set("q", q.trim());
  params.set("limit", String(limit));
  const query = params.toString();
  const list = await api.get<DevRoleEmployeeSearchHit[]>(
    `/dev/role-switch/employees/search?${query}`,
  );
  return Array.isArray(list) ? list : [];
}

export async function fetchDevRoleUserGroups(
  login: string,
  email?: string | null,
): Promise<string[]> {
  const params = new URLSearchParams();
  const mail = (email || "").trim();
  if (mail) params.set("email", mail);
  const query = params.toString();
  const data = await api.get<{ groups: string[] }>(
    `/dev/role-switch/users/${encodeURIComponent(login)}/groups${query ? `?${query}` : ""}`,
  );
  return Array.isArray(data?.groups) ? data.groups : [];
}

export async function applyDevRoleGroups(groups: string[]): Promise<void> {
  await api.put("/dev/role-switch", { groups });
}

export async function applyDevRoleImpersonation(login: string): Promise<void> {
  await api.put("/dev/role-switch", { impersonate_login: login });
}

export async function applyDevSimulateBirthday(enabled: boolean): Promise<void> {
  await api.put("/dev/role-switch", { simulate_birthday: enabled });
}

export async function applyDevSimulateChristmas(enabled: boolean): Promise<void> {
  await api.put("/dev/role-switch", { simulate_christmas: enabled });
}

export async function applyDevSimulateHalloween(enabled: boolean): Promise<void> {
  await api.put("/dev/role-switch", { simulate_halloween: enabled });
}

export async function applyDevSimulateVacation(enabled: boolean): Promise<void> {
  await api.put("/dev/role-switch", { simulate_vacation: enabled });
}

export async function clearDevRoleSwitch(): Promise<void> {
  await api.del("/dev/role-switch");
}
